import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  ButtonGroup,
  Row,
  Col,
  Table,
  Input,
  Label,
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import moment from "moment";

function ReporteUtilidad() {
  const { user } = useContext(AuthContext);
  // Si no existe la variable (p.ej. Netlify sin actualizar) se arma desde articulos
  const URL_REPORTE_UTILIDAD =
    process.env.REACT_APP_URL_REPORTE_UTILIDAD ||
    (process.env.REACT_APP_URL_ARTICULOS || "").replace(
      /articulos$/,
      "reporteUtilidad"
    );

  let hoy = new Date();
  let hace30 = new Date();
  hace30.setDate(hace30.getDate() - 30);

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(
    moment(hace30).format("YYYY-MM-DD")
  );
  const [selectedFechaFin, setSelectedFechaFin] = useState(
    moment(hoy).format("YYYY-MM-DD")
  );
  const [selectedEmpresa, setSelectedEmpresa] = useState("");
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    setCargando(true);
    axios
      .get(
        `${URL_REPORTE_UTILIDAD}/${selectedFechaInicio}/${selectedFechaFin}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((res) => {
        setData(res.data);
        setCargando(false);
      })
      .catch((err) => {
        console.log(err);
        setCargando(false);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "No se pudo cargar el reporte",
        });
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  const dinero = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n || 0);
  const pct = (n) => `${(n || 0).toFixed(2)}%`;
  const numero = (n) => new Intl.NumberFormat("en-US").format(n || 0);

  // Todo viene etiquetado con idEmpresa, asi que el filtro es instantaneo
  const porEmpresa = data?.porEmpresa ?? [];
  const soloEmpresa = (arr) =>
    selectedEmpresa ? arr.filter((r) => r.idEmpresa === selectedEmpresa) : arr;

  const resumen = useMemo(() => {
    if (!data) return null;
    if (!selectedEmpresa) return data.totales;
    return porEmpresa.find((e) => e.idEmpresa === selectedEmpresa) || null;
  }, [data, selectedEmpresa]);

  const lineas = soloEmpresa(data?.porLinea ?? []);
  const gastos = soloEmpresa(data?.gastos ?? []);

  // Los meses se reagrupan para que "Todas las empresas" sume por mes
  const meses = useMemo(() => {
    const filas = soloEmpresa(data?.porMes ?? []);
    const mapa = {};
    filas.forEach((r) => {
      if (!mapa[r.mes])
        mapa[r.mes] = { mes: r.mes, ventasNetas: 0, costoVentas: 0, gastos: 0, utilidadNeta: 0 };
      mapa[r.mes].ventasNetas += r.ventasNetas;
      mapa[r.mes].costoVentas += r.costoVentas;
      mapa[r.mes].gastos += r.gastos;
      mapa[r.mes].utilidadNeta += r.utilidadNeta;
    });
    return Object.values(mapa).sort((a, b) => (a.mes > b.mes ? 1 : -1));
  }, [data, selectedEmpresa]);

  const topMes = Math.max(1, ...meses.map((m) => Math.abs(m.ventasNetas)));
  const nombreEmpresa = selectedEmpresa
    ? porEmpresa.find((e) => e.idEmpresa === selectedEmpresa)?.empresa || ""
    : "Todas las empresas";

  function PDFTabla() {
    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 25, 45, 15);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 125, 8, 30, 25);
    doc.setFontSize(12);
    doc.text(`Reporte de Utilidad`, 20, 38);
    doc.setFontSize(9);
    doc.text(
      `${nombreEmpresa}  |  ${selectedFechaInicio} a ${selectedFechaFin}`,
      20,
      44
    );
    doc.autoTable({
      head: [["Concepto", "Importe"]],
      body: [
        ["Ventas", dinero(resumen?.ventas)],
        ["Devoluciones", `-${dinero(resumen?.devoluciones)}`],
        ["Ventas netas", dinero(resumen?.ventasNetas)],
        ["Costo de ventas", `-${dinero(resumen?.costoVentas)}`],
        ["Utilidad bruta", dinero(resumen?.utilidadBruta)],
        ["Gastos", `-${dinero(resumen?.gastos)}`],
        ["Utilidad neta", dinero(resumen?.utilidadNeta)],
        ["Margen neto", pct(resumen?.margenNeto)],
      ],
      startY: 50,
      styles: { fontSize: 8 },
    });
    doc.autoTable({
      head: [
        [
          "Empresa",
          "Ventas netas",
          "Costo",
          "Utilidad bruta",
          "Gastos",
          "Utilidad neta",
          "Margen",
        ],
      ],
      body: soloEmpresa(porEmpresa).map((e) => [
        e.empresa,
        dinero(e.ventasNetas),
        dinero(e.costoVentas),
        dinero(e.utilidadBruta),
        dinero(e.gastos),
        dinero(e.utilidadNeta),
        pct(e.margenNeto),
      ]),
      startY: doc.lastAutoTable.finalY + 8,
      styles: { fontSize: 8 },
    });
    doc.save(`ReporteUtilidad_${selectedFechaInicio}_${selectedFechaFin}.pdf`);
  }

  function excel() {
    const columnas = [
      "Empresa",
      "Ventas",
      "Devoluciones",
      "VentasNetas",
      "CostoVentas",
      "UtilidadBruta",
      "Gastos",
      "UtilidadNeta",
      "MargenNeto",
      "Piezas",
    ];
    const option = {
      fileName: `ReporteUtilidad_${selectedFechaInicio}_${selectedFechaFin}`,
      datas: [
        {
          sheetData: soloEmpresa(porEmpresa).map((e) => ({
            Empresa: e.empresa,
            Ventas: e.ventas,
            Devoluciones: e.devoluciones,
            VentasNetas: e.ventasNetas,
            CostoVentas: e.costoVentas,
            UtilidadBruta: e.utilidadBruta,
            Gastos: e.gastos,
            UtilidadNeta: e.utilidadNeta,
            MargenNeto: e.margenNeto,
            Piezas: e.piezas,
          })),
          sheetName: "Utilidad",
          sheetFilter: columnas,
          sheetHeader: columnas,
        },
        {
          sheetData: lineas.map((l) => ({
            Empresa: l.empresa,
            Linea: l.linea,
            Piezas: l.piezas,
            VentasNetas: l.ventasNetas,
            CostoVentas: l.costoVentas,
            UtilidadBruta: l.utilidadBruta,
            MargenBruto: l.margenBruto,
          })),
          sheetName: "Por linea",
          sheetFilter: ["Empresa", "Linea", "Piezas", "VentasNetas", "CostoVentas", "UtilidadBruta", "MargenBruto"],
          sheetHeader: ["Empresa", "Linea", "Piezas", "VentasNetas", "CostoVentas", "UtilidadBruta", "MargenBruto"],
        },
        {
          sheetData: gastos.map((g) => ({
            Fecha: g.fecha,
            Empresa: g.empresa,
            Descripcion: g.descripcion,
            Subtotal: g.subtotal,
            IVA: g.iva,
            Total: g.total,
          })),
          sheetName: "Gastos",
          sheetFilter: ["Fecha", "Empresa", "Descripcion", "Subtotal", "IVA", "Total"],
          sheetHeader: ["Fecha", "Empresa", "Descripcion", "Subtotal", "IVA", "Total"],
        },
      ],
    };
    new ExportJsonExcel(option).saveExcel();
  }

  const tarjeta = (titulo, valor, color, nota) => (
    <Col md={2}>
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderLeft: `4px solid ${color}`,
          borderRadius: 10,
          padding: "12px 14px",
          height: "100%",
        }}
      >
        <div
          style={{
            fontSize: "0.7rem",
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontWeight: 700,
          }}
        >
          {titulo}
        </div>
        <div style={{ fontSize: "1.25rem", fontWeight: 700, color }}>{valor}</div>
        {nota ? (
          <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{nota}</div>
        ) : null}
      </div>
    </Col>
  );

  return (
    <>
      <Header />
      <br />
      <br />
      {user.inventarios_costosAdmin ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuAdmin"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col md={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Reporte de Utilidad</h3>
          <h6 align="center" style={{ color: "#64748b" }}>
            {nombreEmpresa} · {selectedFechaInicio} a {selectedFechaFin}
          </h6>

          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label>Empresa</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedEmpresa}
                onChange={(e) => setSelectedEmpresa(e.target.value)}
              >
                <option value="">Todas las empresas</option>
                {porEmpresa
                  .slice()
                  .sort((a, b) => (a.empresa > b.empresa ? 1 : -1))
                  .map((e) => (
                    <option key={e.idEmpresa} value={e.idEmpresa}>{e.empresa}</option>
                  ))}
              </Input>
            </Col>
          </Row>
          <br />

          {cargando ? (
            <h6>
              <i
                className="fas fa-hourglass-half custom-spin"
                style={{ marginRight: 10 }}
              ></i>
              Calculando...
            </h6>
          ) : undefined}

          {resumen ? (
            <>
              <Row>
                {tarjeta("Ventas netas", dinero(resumen.ventasNetas), "#3b82f6",
                  `${numero(resumen.piezas)} pzas · devol. ${dinero(resumen.devoluciones)}`)}
                {tarjeta("Costo de ventas", dinero(resumen.costoVentas), "#64748b")}
                {tarjeta("Utilidad bruta", dinero(resumen.utilidadBruta), "#10b981",
                  `margen ${pct(resumen.margenBruto)}`)}
                {tarjeta("Gastos", dinero(resumen.gastos), "#f97316",
                  `${gastos.length} gasto(s) del periodo`)}
                {tarjeta(
                  "Utilidad neta",
                  dinero(resumen.utilidadNeta),
                  resumen.utilidadNeta >= 0 ? "#16a34a" : "#dc2626",
                  `margen ${pct(resumen.margenNeto)}`
                )}
              </Row>
              <br />

              {/* Como se llega de la venta a la utilidad */}
              <Row>
                <Col md={5}>
                  <div style={{ borderLeft: "4px solid #3b82f6", paddingLeft: 12, margin: "8px 0", color: "#1e3a8a", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase" }}>
                    Del ingreso a la utilidad
                  </div>
                  <Table size="sm" borderless>
                    <tbody>
                      <tr><td>Ventas</td><td align="right">{dinero(resumen.ventas)}</td></tr>
                      <tr><td>Devoluciones</td><td align="right" style={{ color: "#dc2626" }}>-{dinero(resumen.devoluciones)}</td></tr>
                      <tr style={{ borderTop: "1px solid #e2e8f0" }}><td><b>Ventas netas</b></td><td align="right"><b>{dinero(resumen.ventasNetas)}</b></td></tr>
                      <tr><td>Costo de ventas</td><td align="right" style={{ color: "#dc2626" }}>-{dinero(resumen.costoVentas)}</td></tr>
                      <tr style={{ borderTop: "1px solid #e2e8f0" }}><td><b>Utilidad bruta</b></td><td align="right"><b>{dinero(resumen.utilidadBruta)}</b></td></tr>
                      <tr><td>Gastos del periodo</td><td align="right" style={{ color: "#dc2626" }}>-{dinero(resumen.gastos)}</td></tr>
                      <tr style={{ borderTop: "2px solid #0f172a" }}>
                        <td><b>Utilidad neta</b></td>
                        <td align="right" style={{ color: resumen.utilidadNeta >= 0 ? "#16a34a" : "#dc2626" }}>
                          <b>{dinero(resumen.utilidadNeta)}</b>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>

                <Col md={7}>
                  <div style={{ borderLeft: "4px solid #3b82f6", paddingLeft: 12, margin: "8px 0", color: "#1e3a8a", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase" }}>
                    Comparativo por empresa
                  </div>
                  <Table size="sm" striped borderless responsive className="table-responsive-xl">
                    <thead>
                      <tr>
                        <th>Empresa</th>
                        <th align="right">Ventas netas</th>
                        <th align="right">Costo</th>
                        <th align="right">Utilidad bruta</th>
                        <th align="right">Gastos</th>
                        <th align="right">Utilidad neta</th>
                        <th align="right">Margen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {soloEmpresa(porEmpresa).map((e) => (
                        <tr key={e.idEmpresa}>
                          <td>{e.empresa}</td>
                          <td align="right">{dinero(e.ventasNetas)}</td>
                          <td align="right">{dinero(e.costoVentas)}</td>
                          <td align="right">{dinero(e.utilidadBruta)}</td>
                          <td align="right">{dinero(e.gastos)}</td>
                          <td align="right" style={{ color: e.utilidadNeta >= 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                            {dinero(e.utilidadNeta)}
                          </td>
                          <td align="right">{pct(e.margenNeto)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <Row>
                <Col md={7}>
                  <div style={{ borderLeft: "4px solid #3b82f6", paddingLeft: 12, margin: "8px 0", color: "#1e3a8a", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase" }}>
                    Utilidad por linea
                  </div>
                  <Table size="sm" striped borderless responsive className="table-responsive-xl">
                    <thead>
                      <tr>
                        <th>Linea</th>
                        {!selectedEmpresa ? <th>Empresa</th> : null}
                        <th align="right">Pzas</th>
                        <th align="right">Ventas netas</th>
                        <th align="right">Costo</th>
                        <th align="right">Utilidad bruta</th>
                        <th align="right">Margen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineas.map((l) => (
                        <tr key={l.idEmpresa + l.linea}>
                          <td>{l.linea}</td>
                          {!selectedEmpresa ? <td>{l.empresa}</td> : null}
                          <td align="right">{numero(l.piezas)}</td>
                          <td align="right">{dinero(l.ventasNetas)}</td>
                          <td align="right">{dinero(l.costoVentas)}</td>
                          <td align="right" style={{ fontWeight: 600 }}>{dinero(l.utilidadBruta)}</td>
                          <td align="right">{pct(l.margenBruto)}</td>
                        </tr>
                      ))}
                      {!lineas.length ? (
                        <tr><td colSpan="7" style={{ color: "#94a3b8" }}>Sin ventas en el periodo.</td></tr>
                      ) : null}
                    </tbody>
                  </Table>
                </Col>

                <Col md={5}>
                  <div style={{ borderLeft: "4px solid #3b82f6", paddingLeft: 12, margin: "8px 0", color: "#1e3a8a", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase" }}>
                    Evolucion mensual
                  </div>
                  <Table size="sm" borderless responsive className="table-responsive-xl">
                    <thead>
                      <tr>
                        <th>Mes</th>
                        <th align="right">Ventas netas</th>
                        <th align="right">Gastos</th>
                        <th align="right">Utilidad neta</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {meses.map((m) => (
                        <tr key={m.mes}>
                          <td>{m.mes}</td>
                          <td align="right">{dinero(m.ventasNetas)}</td>
                          <td align="right">{dinero(m.gastos)}</td>
                          <td align="right" style={{ color: m.utilidadNeta >= 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                            {dinero(m.utilidadNeta)}
                          </td>
                          <td style={{ width: 110 }}>
                            <div style={{ background: "#f1f5f9", borderRadius: 4, height: 10 }}>
                              <div
                                style={{
                                  width: `${Math.min(100, (Math.abs(m.utilidadNeta) / topMes) * 100)}%`,
                                  background: m.utilidadNeta >= 0 ? "#16a34a" : "#dc2626",
                                  height: 10,
                                  borderRadius: 4,
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!meses.length ? (
                        <tr><td colSpan="5" style={{ color: "#94a3b8" }}>Sin movimientos.</td></tr>
                      ) : null}
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <div style={{ borderLeft: "4px solid #f97316", paddingLeft: 12, margin: "8px 0", color: "#9a3412", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase" }}>
                Gastos descontados en el periodo
              </div>
              <Table size="sm" striped borderless responsive className="table-responsive-xl">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Empresa</th>
                    <th>Descripcion</th>
                    <th align="right">Subtotal</th>
                    <th align="right">IVA</th>
                    <th align="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {gastos.map((g) => (
                    <tr key={g._id}>
                      <td>{g.fecha}</td>
                      <td>{g.empresa}</td>
                      <td>{g.descripcion}</td>
                      <td align="right">{dinero(g.subtotal)}</td>
                      <td align="right">{dinero(g.iva)}</td>
                      <td align="right" style={{ fontWeight: 600 }}>{dinero(g.total)}</td>
                    </tr>
                  ))}
                  {!gastos.length ? (
                    <tr><td colSpan="6" style={{ color: "#94a3b8" }}>Sin gastos en el periodo.</td></tr>
                  ) : null}
                  {gastos.length ? (
                    <tr style={{ borderTop: "1px solid #e2e8f0" }}>
                      <td colSpan="5" align="right"><b>Total gastos</b></td>
                      <td align="right"><b>{dinero(resumen.gastos)}</b></td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </>
          ) : undefined}
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default ReporteUtilidad;
